import React, { useCallback } from 'react';
import { FolderState } from '../../../types/index';
import { ChildBoard } from '../../../types';
import { useParams } from 'react-router-dom';
import { EntityTypes } from '../../../constants';
import { useInboxBoardsArray } from '../../../services/hooks/useEntity';
import type Story from '../../../models/Story';
import { useAddFoldersToStories, useRemoveFoldersFromStories } from '../../../services/hooks/usePosts';
import { useAddFolder } from '../../../services/hooks/useFolders';
import { useStoriesContext } from '../../../services/hooks/useStoriesContext';

interface ChildProps {
  folders: FolderState[];
  initialFolders: FolderState[];
  handleBoardApply: (boardsToAdd: ChildBoard[], boardsToRemove: ChildBoard[]) => void;
  handleNewBoardCreate: (name: string, stories: string[]) => void;
  currentBoardId: string;
}

interface BoardPinnerProviderProps {
  stories: Story[];
  children: (props: ChildProps) => JSX.Element;
}

const BoardPinnerProvider: React.FC<BoardPinnerProviderProps> = ({ stories, children }) => {
  const { entityId = '', boardId: currentBoardId = '' } = useParams();
  const { queryKey } = useStoriesContext();
  const folders = useInboxBoardsArray({ entityId }) as FolderState[];
  const storyBoards = stories.map((story) => Object.keys(story?.folders || {})).flat();
  const initialFolders = folders.filter((folder: FolderState) => storyBoards.includes(folder._id));
  const storyIds = stories.map((story) => story.id);
  const addFoldersToStories = useAddFoldersToStories({ entityId, entityType: EntityTypes.BOARD, queryKey });
  const removeFoldersFromStories = useRemoveFoldersFromStories({ entityId, entityType: EntityTypes.BOARD, queryKey });
  const { mutate: addFolder } = useAddFolder({ entityId, queryKey });

  const handleBoardApply = useCallback(
    (addBoards: ChildBoard[], removeBoards: ChildBoard[]) => {
      if (removeBoards.length) {
        removeFoldersFromStories({ storyIds, folderIds: removeBoards.map((board) => board._id) });
      }

      if (addBoards.length) {
        addFoldersToStories({ storyIds, folderIds: addBoards.map((board) => board._id) });
      }
    },
    [stories],
  );

  const handleNewBoardCreate = useCallback(
    (name: string) => {
      addFolder({ name, storyIds });
    },
    [stories],
  );

  return children({ folders, initialFolders, handleBoardApply, handleNewBoardCreate, currentBoardId });
};

export default BoardPinnerProvider;
